import { batch, SignalObject, signalObject } from "omi";

import { StepTocStructure } from "@/editor/extensions/tableOfContents/models";

import { TocItem } from "../models";

export interface TocStore {
  /**
   * The current TOC items with UI state
   */
  items: TocItem[];

  /**
   * ID of the currently focused step
   */
  focusedStepId: string | undefined;

  /**
   * ID of the currently focused child item
   */
  focusedChildId: string | undefined;

  /**
   * Whether the TOC panel is pinned open
   */
  isPinned: boolean;

  /**
   * ID of the last bookmarked node that was added to the TOC
   */
  lastestBookmarkNodeId: string | undefined;
}

export interface TocActions {
  /**
   * Updates the store with a new TOC structure
   * @param structure - The new TOC structure
   */
  updateFromStructure: (structure: StepTocStructure) => void;

  /**
   * Sets the focus to a specific step
   * @param stepId - ID of the step to focus
   */
  setFocusedStep: (stepId: string | undefined) => void;

  /**
   * Sets the focus to a specific step
   * @param stepId - ID of the step to focus
   */
  setLatestBookmark: (nodeId: string | undefined) => void;

  /**
   * Sets the focus to a specific child item
   * @param childId - ID of the child item to focus
   */
  setFocusedChild: (childId: string | undefined) => void;

  /**
   * Toggles whether a step is open or closed
   * @param stepId - ID of the step to toggle
   * @param isOpen - Whether the step should be open
   */
  toggleStep: (stepId: string, isOpen: boolean) => void;

  /**
   * Toggles the visibility of a child item
   * @param stepId - ID of the parent step
   * @param childId - ID of the child item
   * @param isVisible - Whether the child should be visible
   */
  toggleChildVisibility: (stepId: string, childId: string, isVisible: boolean) => void;

  /**
   * Toggles whether the TOC panel is pinned open
   * @param isPinned - Whether the panel should be pinned
   */
  setPinned: (isPinned: boolean) => void;
}

const store = signalObject<TocStore>({
  items: [],
  focusedStepId: undefined,
  focusedChildId: undefined,
  lastestBookmarkNodeId: undefined,
  isPinned: false,
});

export function useTocStore(): SignalObject<TocStore> & TocActions {
  return {
    ...store,

    updateFromStructure: (structure: StepTocStructure) => {
      const currentItems = store.items.peek();
      const newItems = convertStructureToUiItems(structure, currentItems, store.lastestBookmarkNodeId.value);
      store.items.value = newItems;
    },

    setFocusedStep: (stepId: string | undefined) => {
      store.focusedStepId.value = stepId;
    },

    setFocusedChild: (childId: string | undefined) => {
      store.focusedChildId.value = childId;
    },

    toggleStep: (stepId: string, isOpen: boolean) => {
      const items = [...store.items.value];
      const item = items.find(item => item.id === stepId);

      if (item) {
        item.isOpen = isOpen;
        batch(() => {
          store.items.value = items;
          store.lastestBookmarkNodeId.value = undefined;
        });
      }
    },

    toggleChildVisibility: (stepId: string, childId: string, isVisible: boolean) => {
      const items = [...store.items.value];
      const item = items.find(item => item.id === stepId);

      if (item) {
        item.childrenVisibility = {
          ...item.childrenVisibility,
          [childId]: isVisible,
        };
        store.items.value = items;
      }
    },

    setPinned: (isPinned: boolean) => {
      store.isPinned.value = isPinned;
    },

    setLatestBookmark: (nodeId: string | undefined) => {
      store.lastestBookmarkNodeId.value = nodeId;
    },
  };
}

/**
 * Converts a StepTocStructure to TocUiItems, preserving existing UI state where possible
 *
 * @param structure - The TOC structure from the extension
 * @param existingItems - Any existing UI items to preserve state from
 * @returns Array of TocUiItems with the structure data and UI state
 */
function convertStructureToUiItems(
  structure: StepTocStructure,
  existingItems: TocItem[] = [],
  latestBookmarkId: string | undefined,
): TocItem[] {
  return structure.steps.map(step => {
    const existingItem = existingItems.find(item => item.id === step.id);

    // Initialize the visibility map for children
    const childrenVisibility: Record<string, boolean> = {};
    step.children.forEach(child => {
      const existingVisibility = existingItem?.childrenVisibility[child.id];
      childrenVisibility[child.id] = existingVisibility !== undefined ? existingVisibility : true;
    });

    const hasLatestBookmarkChild = step.children.some(child => child.id === latestBookmarkId);

    return {
      ...step,
      isOpen: hasLatestBookmarkChild || existingItem?.isOpen || false,
      childrenVisibility,
    };
  });
}
